import React, {useCallback} from "react";
import {getMerchantOverview} from "../../api";
import {useAuth} from "../../auth";
import {
  MerchantMovementsList,
  MerchantOrdersList,
  MerchantSettlementSummary,
} from "../../components";
import {useMerchantQuery} from "../../hooks";
import {formatMerchantDate} from "../../utils";

export function MerchantOverviewPage(): JSX.Element {
  const {token} = useAuth();
  const request = useCallback(
    (signal: AbortSignal) => getMerchantOverview(token, signal),
    [token],
  );
  const query = useMerchantQuery(request, "merchant-overview", {
    enabled: Boolean(token),
  });

  return (
    <div className="page merchant-page merchant-overview-page">
      <header className="page-header merchant-page-header">
        <div>
          <h1>Обзор</h1>
          <p className="page-subtitle">
            {query.data?.merchant.name ?? "Баланс и последние операции"}
          </p>
        </div>
        <div className="merchant-refresh-block">
          <span className="merchant-updated-at">
            {query.lastSuccessfulAt
              ? `Обновлено ${formatMerchantDate(query.lastSuccessfulAt)}`
              : "Данные ещё не загружены"}
          </span>
          <button
            className="btn btn-ghost"
            type="button"
            onClick={query.refresh}
            disabled={query.loading || query.refreshing}
          >
            {query.refreshing ? "Обновление…" : "Обновить"}
          </button>
        </div>
      </header>

      {query.stale && query.error && (
        <div className="alert alert-warning merchant-stale-banner" role="status">
          {query.error} Показана последняя успешная версия данных.
          <button className="btn-link-clear" type="button" onClick={query.refresh}>
            Повторить
          </button>
        </div>
      )}

      {query.loading && !query.data && <MerchantOverviewSkeleton/>}

      {!query.loading && !query.data && query.error && (
        <section className="merchant-error-state" role="alert">
          <h2>Не удалось загрузить обзор</h2>
          <p>{query.error}</p>
          <button className="btn btn-primary" type="button" onClick={query.refresh}>
            Повторить
          </button>
        </section>
      )}

      {query.data && (
        <>
          <MerchantSettlementSummary overview={query.data}/>

          <section className="merchant-section-card">
            <div className="section-header merchant-section-header">
              <div>
                <h2>Последние заявки</h2>
                <span>10 последних заявок по времени создания</span>
              </div>
              <a className="btn btn-ghost btn-sm" href="#/merchant/orders">
                Все заявки
              </a>
            </div>
            <MerchantOrdersList
              items={query.data.recentOrders}
              emptyMessage="Заявок пока нет."
            />
          </section>

          <section className="merchant-section-card">
            <div className="section-header merchant-section-header">
              <div>
                <h2>Последние движения</h2>
                <span>Последние записи append-only выписки</span>
              </div>
              <a className="btn btn-ghost btn-sm" href="#/merchant/balance">
                Все движения
              </a>
            </div>
            <MerchantMovementsList
              items={query.data.recentMovements}
              emptyMessage="Движений по балансу пока нет."
            />
          </section>
        </>
      )}
    </div>
  );
}

function MerchantOverviewSkeleton(): JSX.Element {
  return (
    <div className="merchant-skeleton" aria-label="Загрузка обзора" aria-busy="true">
      <div className="merchant-skeleton-grid">
        <span/>
        <span/>
        <span/>
      </div>
      <div className="merchant-skeleton-panel"/>
      <div className="merchant-skeleton-panel"/>
    </div>
  );
}
